import React, { useEffect, useState, useRef, ChangeEvent, FormEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, collection, query, where, getDocs, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, auth, storage } from '../services/firebase';
import { Container, Form, Button, Alert, Spinner, Badge } from 'react-bootstrap';
import { resizeImage, validateImageFile } from '../utils/imageUtils';
import { Member } from '../../../shared/types';

interface FormData extends Member {
  [key: string]: any;
}

function MemberEdit({ isNew = false }) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [member, setMember] = useState<Member | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [formData, setFormData] = useState<FormData | null>(null);
  const [isSuperUser, setIsSuperUser] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    const fetchMemberAndCheckPermissions = async () => {
      try {
        setLoading(true);
        setError(null);

        let superUserStatus = false;
        // First check if user is super user
        if (auth.currentUser?.email === 'jared@heytheremedia.com') {
          const superUsersRef = collection(db, 'super_users');
          const q = query(superUsersRef, where('email', '==', auth.currentUser.email));
          const querySnapshot = await getDocs(q);
          superUserStatus = !querySnapshot.empty;
          setIsSuperUser(superUserStatus);
        }

        // If creating new member, only super users can do it
        if (isNew) {
          if (!superUserStatus) {
            navigate('/members', { 
              state: { error: 'Only super users can create new members' }
            });
            return;
          }
          setLoading(false);
          return;
        }

        // Then fetch member data for editing
        if (!id) {
          setError('No member ID provided');
          setLoading(false);
          return;
        }

        const memberDocRef = doc(db, 'members', id);
        const docSnap = await getDoc(memberDocRef);
        
        if (docSnap.exists()) {
          const memberData = docSnap.data() as Member;
          
          // Check permissions - allow if own profile or super user
          const isOwnProfile = auth.currentUser?.email?.toLowerCase() === memberData.Email?.toLowerCase();
          
          if (!isOwnProfile && !superUserStatus) {
            navigate('/members', { 
              state: { error: 'You can only edit your own information' }
            });
            return;
          }

          setMember(memberData);
          setFormData(memberData as FormData);
        } else {
          setError('Member not found');
        }
      } catch (err) {
        console.error('Error fetching member details:', err);
        setError('Error loading member details');
      } finally {
        setLoading(false);
      }
    };

    fetchMemberAndCheckPermissions();
  }, [id, navigate, isNew]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => prev ? ({
      ...prev,
      [name]: value
    }) : null);
  };

  const handlePhotoUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploadingPhoto(true);
      validateImageFile(file);
      setPhotoFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('Error processing image');
      }
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!formData) return;

    setSaving(true);
    setError(null);

    try {
      let photoURL = formData.photoURL;

      if (photoFile) {
        // Resize image before upload
        const resizedBlob = await resizeImage(photoFile);
        
        // Upload to Firebase Storage
        const storageRef = ref(storage, `member-photos/${formData.Email}/${photoFile.name}`);
        const uploadResult = await uploadBytes(storageRef, resizedBlob);
        photoURL = await getDownloadURL(uploadResult.ref);
      }

      const memberData = {
        ...formData,
        photoURL,
        updatedAt: new Date().toISOString(),
        updatedBy: auth.currentUser?.email || 'unknown'
      };

      if (isNew) {
        memberData.createdAt = new Date().toISOString();
        memberData.createdBy = auth.currentUser?.email || 'unknown';
      }

      // Remove id from data before saving
      const { id: _, ...dataToSave } = memberData;

      // Save to Firestore
      if (!id && !memberData.Email) {
        throw new Error('No member ID or email provided');
      }

      const memberRef = doc(db, 'members', id || memberData.Email);
      await setDoc(memberRef, dataToSave, { merge: true });

      navigate(`/members/${memberRef.id}`);
    } catch (error) {
      console.error('Error saving member:', error);
      setError(error instanceof Error ? error.message : 'Error saving member');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <Alert variant="danger">{error}</Alert>;

  return (
    <Container style={{ maxWidth: '800px', padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <Button 
          variant="outline-primary" 
          onClick={() => navigate('/members')}
        >
          ← Back to Members
        </Button>
        {isSuperUser && (
          <Badge bg="warning" text="dark" className="ms-2">Super User Mode</Badge>
        )}
      </div>

      <h2>{isNew ? 'Create New Member' : 'Edit Member Information'}</h2>
      {isSuperUser && member && member.Email !== auth.currentUser?.email && (
        <Alert variant="info" className="mb-3">
          <i className="bi bi-shield-check me-2"></i>
          You are editing {member.Name}'s profile as a super user
        </Alert>
      )}

      <Form onSubmit={handleSubmit}>
        {/* Photo Upload Section */}
        <div className="mb-4">
          <div
            style={{
              width: '200px',
              height: '200px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '10px',
              position: 'relative'
            }}
          >
            {formData?.photoURL ? (
              <img 
                src={formData.photoURL} 
                alt={formData.Name} 
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            ) : (
              <div style={{ color: '#adb5bd', textAlign: 'center' }}>
                <i className="bi bi-person" style={{ fontSize: '3rem' }}></i>
                <div>No Photo</div>
              </div>
            )}
          </div>
          <div>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              accept="image/jpeg,image/png,image/heic"
              onChange={handlePhotoUpload}
            />
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingPhoto}
              className="w-100 mb-2"
            >
              {uploadingPhoto ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Uploading...
                </>
              ) : (
                <>
                  <i className="bi bi-camera me-2"></i>
                  {formData?.photoURL ? 'Change Photo' : 'Add Photo'}
                </>
              )}
            </Button>
            {formData?.photoURL && !uploadingPhoto && (
              <Button
                type="submit"
                variant="primary"
                size="sm"
                className="w-100"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            )}
          </div>
        </div>

        <Form.Group className="mb-3">
          <Form.Label>Name</Form.Label>
          <Form.Control
            type="text"
            name="Name"
            value={formData?.Name || ''}
            onChange={handleInputChange}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Title</Form.Label>
          <Form.Control
            type="text"
            name="Title"
            value={formData?.Title || ''}
            onChange={handleInputChange}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Company</Form.Label>
          <Form.Control
            type="text"
            name="Company"
            value={formData?.Company || ''}
            onChange={handleInputChange}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Phone Number</Form.Label>
          <Form.Control
            type="text"
            name="Phone Number"
            value={formData?.['Phone Number'] || ''}
            onChange={handleInputChange}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Instagram</Form.Label>
          <Form.Control
            type="text"
            name="Instagram"
            value={formData?.Instagram || ''}
            onChange={handleInputChange}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Flight Number</Form.Label>
          <Form.Control
            type="text"
            name="Flight # incoming"
            value={formData?.['Flight # incoming'] || ''}
            onChange={handleInputChange}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Flight Arrival Time</Form.Label>
          <Form.Control
            type="text"
            name="Flight arrival time"
            value={formData?.['Flight arrival time'] || ''}
            onChange={handleInputChange}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Flight Departure Time</Form.Label>
          <Form.Control
            type="text"
            name="Flight departure time"
            value={formData?.['Flight departure time'] || ''}
            onChange={handleInputChange}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Car Rental</Form.Label>
          <Form.Control
            type="text"
            name="Car Rented?"
            value={formData?.['Car Rented?'] || ''}
            onChange={handleInputChange}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Travel Insurance Policy</Form.Label>
          <Form.Control
            type="text"
            name="Travel Insurance Policy?"
            value={formData?.['Travel Insurance Policy?'] || ''}
            onChange={handleInputChange}
          />
        </Form.Group>

        <div className="d-flex justify-content-end gap-2">
          <Button 
            variant="secondary" 
            onClick={() => navigate('/members')}
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="primary"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </Form>
    </Container>
  );
}

export default MemberEdit; 